/**
 * Ops signal strength from effect size + sample size.
 * Not a formal p-value — transparent heuristic so alerts recalculate with the data window.
 */

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/** Map |delta %| → 0..1 effect (≈5%→0.34, 15%→0.71, 30%→0.92). */
const effectFromDeltaPct = (absDeltaPct) => 1 - Math.exp(-Math.abs(absDeltaPct) / 12);

/** Log sample confidence toward ~500 events. */
const sampleFactor = (sampleSize) => {
  const n = Math.max(0, Number(sampleSize) || 0);
  if (n <= 0) return 0.4;
  return clamp(Math.log10(n + 1) / Math.log10(500), 0.2, 1);
};

/**
 * @returns {{ good: boolean, probability: number, strength: string, deltaPct: number } | null}
 */
export const scoreDeltaSignal = ({
  deltaPct,
  sampleSize = 0,
  higherIsGood = true,
  minAbsDelta = 0.25,
} = {}) => {
  const delta = Number(deltaPct);
  if (!Number.isFinite(delta)) return null;
  if (Math.abs(delta) < minAbsDelta) {
    return {
      good: true,
      probability: Math.round(40 * sampleFactor(sampleSize)),
      strength: 'flat',
      deltaPct: delta,
    };
  }
  const effect = effectFromDeltaPct(delta);
  const probability = Math.round(clamp(effect * sampleFactor(sampleSize) * 100, 8, 99));
  const good = higherIsGood ? delta >= 0 : delta <= 0;
  let strength = 'moderate';
  if (probability >= 75) strength = 'strong';
  else if (probability < 40) strength = 'weak';
  return { good, probability, strength, deltaPct: delta };
};

/**
 * Score uplift vs baseline expected volume.
 * @returns {{ good: boolean, probability: number, strength: string, deltaPct: number } | null}
 */
export const scoreUpliftSignal = ({
  actual,
  expected,
  sampleSize,
  higherIsGood = true,
} = {}) => {
  const a = Number(actual);
  const e = Number(expected);
  if (!Number.isFinite(a) || !Number.isFinite(e)) return null;
  if (e === 0) {
    if (a === 0) return null;
    return scoreDeltaSignal({
      deltaPct: a > 0 ? 100 : -100,
      sampleSize: sampleSize ?? a,
      higherIsGood,
    });
  }
  const deltaPct = ((a - e) / Math.abs(e)) * 100;
  return scoreDeltaSignal({
    deltaPct,
    sampleSize: sampleSize ?? Math.max(a, e),
    higherIsGood,
  });
};

/** Rate vs previous equal window (lower or higher better). */
export const scoreRateDelta = ({
  current,
  previous,
  sampleSize,
  higherIsGood = false,
} = {}) => {
  const cur = Number(current);
  const prev = Number(previous);
  if (!Number.isFinite(cur) || !Number.isFinite(prev) || prev === 0) {
    if (!Number.isFinite(cur)) return null;
    // No baseline: confidence from sample only; tone from rate extremity toward worse end.
    const extremity = higherIsGood ? cur / 100 : (100 - cur) / 100;
    const probability = Math.round(clamp(sampleFactor(sampleSize) * Math.abs(0.5 - extremity) * 160, 10, 85));
    const good = higherIsGood ? cur >= 50 : cur <= 50;
    return {
      good,
      probability,
      strength: probability >= 60 ? 'moderate' : 'weak',
      deltaPct: null,
    };
  }
  const deltaPct = ((cur - prev) / Math.abs(prev)) * 100;
  return scoreDeltaSignal({ deltaPct, sampleSize, higherIsGood });
};

export const formatProbability = (probability) => {
  if (probability == null || !Number.isFinite(Number(probability))) return null;
  return `${Math.round(Number(probability))}% conf.`;
};
